import { ReactElement } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore, useSystemManagerStore } from '@editorStores'
import { useResultsInvalidation } from '@editorHooks'
import {
  getModel,
  getAssemblies,
  getProjectAssemblies,
  getProjectAssemblyExportDocument,
} from '@queries'
import { downLoadFile } from '@utils'
import { buildErrorMessage } from 'src/constants/errors'
import {
  createAssembly,
  createProjectAssembly,
  deleteProjectAssembly,
  editProjectAssembly,
  setStandardAssembly,
} from 'src/state/mutations/assemblies'
import AssemblySelection from '../AssemblySelection'

interface Props {
  earthMassForce: number
}

const AssemblyManagerForm = ({ earthMassForce }: Props): ReactElement => {
  const { projectId } = useParams()
  const queryClient = useQueryClient()
  const invalidateResults = useResultsInvalidation()

  const assemblies = useSystemManagerStore(state => state.assemblies)
  const assignments = useSystemManagerStore(state => state.assemblyAssignment)
  const project = useModelStore(state => state.project)

  const { enqueueSnackbar } = useSnackbar()

  const { mutate: setStandard, isLoading: isSettingStandard } = useMutation(
    (assembly: Assembly) => setStandardAssembly.request(projectId as string, assembly.guid),
    {
      onSuccess: async projectAssemblies => {
        queryClient.setQueryData(getProjectAssemblies.getKey(projectId), projectAssemblies)
        await queryClient.invalidateQueries(getModel.getKey(projectId))
        invalidateResults(projectId as string)
        enqueueSnackbar('Standardaufbau erfolgreich gespeichert', {
          variant: 'success',
        })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Standardaufbaus'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: onDelete } = useMutation(
    (assemblyId: string) => deleteProjectAssembly.request(projectId as string, assemblyId),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getProjectAssemblies.getKey(projectId))
        await queryClient.invalidateQueries(getModel.getKey(projectId))
        invalidateResults(projectId as string)
        enqueueSnackbar('Aufbauten erfolgreich gelöscht', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen der Aufbauten'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: downloadExportDocument } = useMutation(
    async (assemblyId: string) => {
      const data = await getProjectAssemblyExportDocument.request(projectId as string, assemblyId)

      return { data, assemblyId }
    },
    {
      onSuccess: async ({ data, assemblyId }) => {
        if (!data) {
          enqueueSnackbar('Ergebnisse noch nicht vorhanden', { variant: 'warning' })
          return
        }

        downLoadFile(
          data,
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          `export_aufbau_${projectId}_${assemblyId}`,
        )
      },
      onError: (e: Error) => {
        enqueueSnackbar(e?.message || 'Fehler beim Abrufen des Dokuments', { variant: 'error' })
      },
    },
  )

  const saveProjectAssemblyToSystemManager = (assembly: Assembly) => {
    const newName = `${assembly.name} of ${project.name}`
    const renamedAssembly: Assembly = {
      ...assembly,
      name: newName,
    }
    return onSaveToSystemManager(renamedAssembly)
  }

  const { mutateAsync: onSaveToSystemManager } = useMutation(
    (assembly: Assembly) => createAssembly.request(assembly),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getAssemblies.key)
        enqueueSnackbar('Aufbau erfolgreich im Systemmanager gespeichert.', {
          variant: 'success',
        })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(
          buildErrorMessage(error, 'Fehler beim Speichern des Aufbaus im Systemmanager'),
          { variant: 'error' },
        )
      },
    },
  )

  const createMutationResult = useMutation(
    (data: Assembly) => createProjectAssembly.request(projectId as string, data),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getProjectAssemblies.getKey(projectId))
        await queryClient.invalidateQueries(getModel.getKey(projectId))
        invalidateResults(projectId as string)

        enqueueSnackbar('Aufbau erfolgreich angelegt', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Aufbaus'), {
          variant: 'error',
        })
      },
    },
  )

  const editMutationResult = useMutation(
    (data: Assembly) => editProjectAssembly.request(projectId as string, data),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getProjectAssemblies.getKey(projectId))
        await queryClient.invalidateQueries(getModel.getKey(projectId))
        invalidateResults(projectId as string)

        enqueueSnackbar('Aufbau erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Aufbaus'), {
          variant: 'error',
        })
      },
    },
  )
  return (
    <>
      <AssemblySelection
        assemblies={assemblies}
        assignments={assignments}
        onSetStandard={setStandard}
        isSettingStandard={isSettingStandard}
        onDelete={assembly => onDelete(assembly.guid)}
        onDownload={async ({ guid }) => {
          await downloadExportDocument(guid)
        }}
        onStore={assembly => saveProjectAssemblyToSystemManager(assembly)}
        editMutation={editMutationResult}
        createMutation={createMutationResult}
        earthMassForce={earthMassForce}
      />
    </>
  )
}

export default AssemblyManagerForm
